import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Check,
  Eye,
  FileClock,
  History,
  IdCard,
  ImageOff,
  LayoutTemplate,
  Loader2,
  Save,
  Settings2,
  Star,
} from 'lucide-react';
import { useSchool } from '../../../context/SchoolContext';
import { idCardApi } from '../services/idCardApi';
import {
  createBlobUrl,
  documentTypeForOwner,
  getApiErrorMessage,
  humanize,
  unwrapApiResponse,
} from '../utils/documentUtils';
import {
  EmptyState,
  ErrorBanner,
  LoadingBlock,
  Modal,
  PageHeader,
  StatusBadge,
} from '../components/DocumentUi';

const deepClone = (value) => JSON.parse(JSON.stringify(value || {}));

const ensureConfigurationShape = (configuration = {}) => ({
  branding: { ...(configuration.branding || {}) },
  field_visibility: { ...(configuration.field_visibility || {}) },
  labels: { ...(configuration.labels || {}) },
  instructions: Array.isArray(configuration.instructions) ? configuration.instructions : [],
  options: { ...(configuration.options || {}) },
});

const TemplateThumbnail = ({ schoolId, template }) => {
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl = '';
    setFailed(false);
    idCardApi
      .getTemplateThumbnail(schoolId, template.id)
      .then((response) => {
        if (!active) return;
        objectUrl = createBlobUrl(response);
        setUrl(objectUrl);
      })
      .catch(() => active && setFailed(true));

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [schoolId, template.id]);

  if (failed) {
    return (
      <div className="flex h-64 items-center justify-center rounded-xl bg-gray-100 text-gray-400">
        <ImageOff className="h-8 w-8" />
      </div>
    );
  }
  if (!url) {
    return <div className="flex h-64 items-center justify-center rounded-xl bg-gray-100"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>;
  }
  return <img src={url} alt={`${template.name} thumbnail`} className="h-64 w-full rounded-xl bg-gray-100 object-contain p-4" />;
};

const TemplatesPage = () => {
  const { schoolId } = useSchool();
  const [ownerType, setOwnerType] = useState('STUDENT');
  const [templates, setTemplates] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [configTemplate, setConfigTemplate] = useState(null);
  const [configuration, setConfiguration] = useState(ensureConfigurationShape());
  const [settingIsDefault, setSettingIsDefault] = useState(false);
  const [loadingSetting, setLoadingSetting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewingId, setPreviewingId] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [makingDefaultId, setMakingDefaultId] = useState('');

  const loadTemplates = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    setError('');
    try {
      const [templateResponse, fieldResponse] = await Promise.all([
        idCardApi.getTemplates(schoolId, documentTypeForOwner(ownerType)),
        idCardApi.getAvailableFields(schoolId, ownerType),
      ]);
      setTemplates(unwrapApiResponse(templateResponse).data || []);
      setFields(unwrapApiResponse(fieldResponse).data || []);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Failed to load ID-card templates.'));
    } finally {
      setLoading(false);
    }
  }, [ownerType, schoolId]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const supportedConfigKeys = useMemo(
    () => new Set((configTemplate?.manifest?.supported_fields || []).map((item) => item.config_key)),
    [configTemplate],
  );

  const configurableFields = useMemo(
    () => fields.filter((field) => supportedConfigKeys.has(field.config_key)),
    [fields, supportedConfigKeys],
  );

  const openConfiguration = async (template) => {
    setConfigTemplate(template);
    setLoadingSetting(true);
    setError('');
    try {
      const response = await idCardApi.getTemplateSettings(schoolId, template.id);
      const setting = unwrapApiResponse(response).data || {};
      const nextConfiguration =
        setting.configuration || template.manifest?.default_configuration || {};
      setConfiguration(ensureConfigurationShape(deepClone(nextConfiguration)));
      setSettingIsDefault(Boolean(setting.is_default));
    } catch (requestError) {
      setConfigTemplate(null);
      setError(getApiErrorMessage(requestError, 'Failed to load template settings.'));
    } finally {
      setLoadingSetting(false);
    }
  };

  const updateConfigSection = (section, key, value) => {
    setConfiguration((previous) => ({
      ...previous,
      [section]: { ...(previous[section] || {}), [key]: value },
    }));
  };

  const saveConfiguration = async () => {
    if (!configTemplate) return;
    setSaving(true);
    setError('');
    try {
      await idCardApi.updateTemplateSettings(schoolId, configTemplate.id, {
        configuration,
        is_default: settingIsDefault,
      });
      setConfigTemplate(null);
      await loadTemplates();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Failed to save template configuration.'));
    } finally {
      setSaving(false);
    }
  };

  const previewTemplate = async (template, customConfiguration) => {
    setPreviewingId(template.id);
    setError('');
    try {
      const response = await idCardApi.previewTemplate(schoolId, template.id, {
        owner_type: ownerType,
        use_sample_data: true,
        configuration:
          customConfiguration ||
          template.school_setting?.configuration ||
          template.manifest?.default_configuration ||
          {},
      });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(createBlobUrl(response));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Could not render the template preview.'));
    } finally {
      setPreviewingId('');
    }
  };

  const makeDefault = async (template) => {
    setMakingDefaultId(template.id);
    setError('');
    try {
      await idCardApi.makeDefaultTemplate(schoolId, template.id);
      await loadTemplates();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Failed to update the default template.'));
    } finally {
      setMakingDefaultId('');
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <PageHeader
        title="ID Card Templates"
        description="Preview predefined templates and configure each school’s branding, visible fields, labels, instructions, and default design."
        actions={
          <>
            <Link to="/documents/id-cards" className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"><IdCard className="h-4 w-4" /> Generate</Link>
            <Link to="/documents/generation-jobs" className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"><FileClock className="h-4 w-4" /> Jobs</Link>
            <Link to="/documents/card-history" className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"><History className="h-4 w-4" /> History</Link>
          </>
        }
      />

      <ErrorBanner message={error} onDismiss={() => setError('')} />

      <div className="flex w-fit rounded-lg border border-gray-200 bg-white p-1">
        {['STUDENT', 'STAFF'].map((type) => (
          <button key={type} type="button" onClick={() => setOwnerType(type)} className={`rounded-md px-5 py-2 text-sm font-semibold ${ownerType === type ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
            {humanize(type)} templates
          </button>
        ))}
      </div>

      {loading ? (
        <div className="rounded-xl border border-gray-200 bg-white"><LoadingBlock label="Loading template gallery..." /></div>
      ) : templates.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white"><EmptyState title="No templates found" description="Run python manage.py sync_document_templates in the backend and reload this page." /></div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {templates.map((template) => {
            const isDefault = Boolean(template.school_setting?.is_default);
            return (
              <article key={template.id} className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                <div className="p-4 pb-0"><TemplateThumbnail schoolId={schoolId} template={template} /></div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="font-bold text-gray-900">{template.name}</h2>
                      <p className="mt-1 text-xs text-gray-500">Version {template.version} · {humanize(template.orientation)} · {template.width_mm} × {template.height_mm} mm</p>
                    </div>
                    {isDefault ? <span className="inline-flex items-center gap-1 rounded-full bg-gray-900 px-2.5 py-1 text-xs font-bold text-white"><Star className="h-3 w-3 fill-current" /> Default</span> : <StatusBadge value={template.status} />}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2 text-xs text-gray-600">
                    <span className="rounded-full bg-gray-100 px-2.5 py-1">{template.is_double_sided ? 'Double-sided' : 'Single-sided'}</span>
                    <span className="rounded-full bg-gray-100 px-2.5 py-1">{template.is_system_template ? 'System template' : 'School template'}</span>
                    <span className="rounded-full bg-gray-100 px-2.5 py-1">QR supported</span>
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-2">
                    <button type="button" disabled={previewingId === template.id} onClick={() => previewTemplate(template)} className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">{previewingId === template.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview</button>
                    <button type="button" onClick={() => openConfiguration(template)} className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"><Settings2 className="h-4 w-4" /> Configure</button>
                    {!isDefault && (
                      <button type="button" disabled={makingDefaultId === template.id} onClick={() => makeDefault(template)} className="col-span-2 inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">{makingDefaultId === template.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Star className="h-4 w-4" />} Make school default</button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Modal
        open={Boolean(configTemplate)}
        onClose={() => !saving && setConfigTemplate(null)}
        title={configTemplate ? `Configure ${configTemplate.name}` : 'Configure template'}
        description="Only safe, backend-supported configuration fields are stored."
        size="max-w-5xl"
        footer={
          <div className="flex items-center justify-between gap-4">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700"><input type="checkbox" checked={settingIsDefault} onChange={(event) => setSettingIsDefault(event.target.checked)} /> Use as school default</label>
            <div className="flex gap-2">
              <button type="button" disabled={saving || !configTemplate} onClick={() => previewTemplate(configTemplate, configuration)} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50"><Eye className="h-4 w-4" /> Preview changes</button>
              <button type="button" disabled={saving} onClick={saveConfiguration} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save configuration</button>
            </div>
          </div>
        }
      >
        {loadingSetting ? (
          <LoadingBlock label="Loading school template settings..." />
        ) : (
          <div className="space-y-8">
            <section>
              <h3 className="font-bold text-gray-900">Branding</h3>
              <p className="mb-3 text-sm text-gray-500">Use the logo, school details, seal, signatures, and colors maintained in School Settings.</p>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {Object.entries(configuration.branding || {}).filter(([key]) => key.startsWith('use_') || key === 'allow_color_override').map(([key, value]) => (
                  <label key={key} className="flex items-center justify-between rounded-lg border border-gray-200 p-3 text-sm"><span className="font-medium text-gray-700">{humanize(key)}</span><input type="checkbox" checked={Boolean(value)} onChange={(event) => updateConfigSection('branding', key, event.target.checked)} /></label>
                ))}
              </div>
              {configuration.branding?.allow_color_override && (
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  {['primary_color_override', 'secondary_color_override'].map((key) => (
                    <label key={key} className="text-sm font-medium text-gray-700">{humanize(key)}<div className="mt-1 flex gap-2"><input type="color" value={configuration.branding?.[key] || '#2563eb'} onChange={(event) => updateConfigSection('branding', key, event.target.value)} className="h-10 w-14 rounded border border-gray-300" /><input value={configuration.branding?.[key] || ''} onChange={(event) => updateConfigSection('branding', key, event.target.value)} placeholder="#2563eb" className="flex-1 rounded-lg border border-gray-300 px-3 py-2" /></div></label>
                  ))}
                </div>
              )}
            </section>

            <section>
              <h3 className="font-bold text-gray-900">Visible fields</h3>
              <p className="mb-3 text-sm text-gray-500">Required generation fields remain enabled.</p>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {configurableFields.map((field) => {
                  const enabled = configuration.field_visibility?.[field.config_key] ?? field.default_enabled;
                  return (
                    <label key={field.config_key} className="flex items-center justify-between rounded-lg border border-gray-200 p-3 text-sm"><span><span className="block font-medium text-gray-800">{field.label}</span><span className="text-xs text-gray-500">{humanize(field.category)} · {humanize(field.type)}</span></span><input type="checkbox" disabled={field.required_for_generation} checked={field.required_for_generation || Boolean(enabled)} onChange={(event) => updateConfigSection('field_visibility', field.config_key, event.target.checked)} /></label>
                  );
                })}
              </div>
            </section>

            <section>
              <h3 className="font-bold text-gray-900">Labels</h3>
              <p className="mb-3 text-sm text-gray-500">Customize printed labels without changing the underlying field keys.</p>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {configurableFields.filter((field) => ['TEXT', 'DATE'].includes(field.type) && !['student_name', 'staff_name', 'school_name'].includes(field.config_key)).map((field) => (
                  <label key={field.config_key} className="text-sm font-medium text-gray-700">{field.label}<input value={configuration.labels?.[field.config_key] ?? field.label} onChange={(event) => updateConfigSection('labels', field.config_key, event.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal" /></label>
                ))}
              </div>
            </section>

            <section className="grid gap-6 lg:grid-cols-2">
              <label className="text-sm font-medium text-gray-700">Back-side instructions<textarea value={(configuration.instructions || []).join('\n')} onChange={(event) => setConfiguration((previous) => ({ ...previous, instructions: event.target.value.split('\n').map((line) => line.trim()).filter(Boolean) }))} rows={7} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal" placeholder="One instruction per line" /></label>
              <div className="space-y-4">
                <h3 className="font-bold text-gray-900">Print options</h3>
                <label className="flex items-center justify-between rounded-lg border border-gray-200 p-3 text-sm"><span className="font-medium text-gray-700">Include back side</span><input type="checkbox" checked={configuration.options?.include_back !== false} onChange={(event) => updateConfigSection('options', 'include_back', event.target.checked)} /></label>
                <label className="block text-sm font-medium text-gray-700">Photo shape<select value={configuration.options?.photo_shape || 'ROUNDED'} onChange={(event) => updateConfigSection('options', 'photo_shape', event.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"><option value="ROUNDED">Rounded</option><option value="CIRCLE">Circle</option><option value="SQUARE">Square</option></select></label>
                <label className="block text-sm font-medium text-gray-700">Duplex flip<select value={configuration.options?.duplex_flip || 'LONG_EDGE'} onChange={(event) => updateConfigSection('options', 'duplex_flip', event.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"><option value="LONG_EDGE">Long edge</option><option value="SHORT_EDGE">Short edge</option></select></label>
              </div>
            </section>
          </div>
        )}
      </Modal>

      <Modal open={Boolean(previewUrl)} onClose={() => { URL.revokeObjectURL(previewUrl); setPreviewUrl(''); }} title="Template PDF preview" description="Sample-data preview generated by the backend Playwright renderer." size="max-w-5xl">
        <iframe title="Template preview" src={previewUrl} className="h-[72vh] w-full rounded-lg border border-gray-200" />
      </Modal>
    </div>
  );
};

export default TemplatesPage;
